import React, { useState } from 'react';
import {
  CamposDoFaturamento, ENVIO_POR_EMAIL, PERFIS_DE_FATURAMENTO, aprovacaoAdiada, motivoDaTrava, oQueOPerfilMuda, resumoDoFaturamento,
} from '../../utils/faturamentoDoCliente';

/**
 * Os interruptores de faturamento da ficha do cliente, em 4 perguntas (fase 1 do plano de empenho, 16/10/2026). Ver
 * utils/faturamentoDoCliente. Os campos e o payload são os de antes: esta peça só lê e escreve no `form` da ficha, e
 * quem grava é o Salvar.
 */
type Props = { form: any; setForm: (f: any) => void; campo: string };

function Interruptor({ id, ligado, onChange, rotulo, dica, trava }: {
  id: string; ligado: boolean; onChange: (v: boolean) => void; rotulo: string; dica: string; trava?: string | null;
}) {
  // Travado só para LIGAR: o que já está ligado continua desligável (a ficha nunca muda o dado sozinha).
  const travado = !!trava && !ligado;
  return (
    <div className='flex items-start gap-3' data-interruptor={id}>
      <label className={`relative inline-flex items-center mt-0.5 ${travado ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
        <input type='checkbox' checked={ligado} disabled={travado} onChange={(e) => onChange(e.target.checked)} className='sr-only peer' aria-label={rotulo} />
        <div className="w-9 h-5 bg-slate-700 peer-focus-visible:ring-2 peer-focus-visible:ring-primary rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary"></div>
      </label>
      <div className='min-w-0'>
        <span className='text-sm text-slate-200'>{rotulo}</span>
        <span className='block text-xs text-slate-500'>{dica}</span>
        {trava && <span className='block text-xs text-amber-300 mt-0.5' data-motivo-da-trava>{trava}</span>}
      </div>
    </div>
  );
}

function Grupo({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <fieldset className='rounded-lg border border-central-borda p-4 min-w-0' data-grupo-do-faturamento={titulo}>
      <legend className='px-1 text-xs font-semibold uppercase tracking-wide text-slate-400'>{titulo}</legend>
      <div className='space-y-3'>{children}</div>
    </fieldset>
  );
}

export default function ConfiguracaoDeFaturamento({ form, setForm, campo }: Props) {
  const f = form as CamposDoFaturamento;
  const [avisoDoPerfil, setAvisoDoPerfil] = useState<string | null>(null);
  const liga = (k: keyof CamposDoFaturamento) => (v: boolean) => setForm({ ...form, [k]: v });
  const adiada = aprovacaoAdiada(f);

  return (
    <div className='space-y-4' data-configuracao-do-faturamento>
      <div className='rounded-lg border border-central-borda bg-central-elevado/60 px-4 py-3'>
        <p className='text-xs font-semibold uppercase tracking-wide text-slate-400'>Como este cliente é faturado</p>
        <p className='text-sm text-white mt-1' data-resumo-do-faturamento>{resumoDoFaturamento(f)}</p>
      </div>

      <div className='flex flex-wrap items-center gap-2'>
        <span className='text-xs text-slate-400'>Começar de um perfil:</span>
        {PERFIS_DE_FATURAMENTO.map((p) => (
          <button key={p.chave} type='button' data-perfil={p.chave}
            onClick={() => {
              const muda = oQueOPerfilMuda(p, f);
              setForm({ ...form, ...p.campos });
              setAvisoDoPerfil(muda.length
                ? `"${p.rotulo}" mudou ${muda.join('; ')}. Nada foi gravado: revise e clique em Salvar.`
                : `Já está como "${p.rotulo}".`);
            }}
            className='px-3 py-1.5 rounded-lg border border-central-borda bg-central-cartao text-xs text-central-texto hover:bg-central-elevado'>
            {p.rotulo}
          </button>
        ))}
      </div>
      {avisoDoPerfil && <p className='text-xs text-slate-300' role='status' data-aviso-do-perfil>{avisoDoPerfil}</p>}

      <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
        <Grupo titulo='Quem paga'>
          <Interruptor id='isPublicSector' ligado={!!f.isPublicSector} onChange={liga('isPublicSector')} rotulo='Órgão Público' dica='vigência do mês anterior' />
          <Interruptor id='paysByFiscalDocument' ligado={!!f.paysByFiscalDocument} onChange={liga('paysByFiscalDocument')} rotulo='Paga por Empenho' dica='cada nota é paga em separado: a conciliação sugere o pagamento parcial pelo valor da nota' />
        </Grupo>
        <Grupo titulo='Como a nota sai'>
          <Interruptor id='splitFiscalInvoices' ligado={!!f.splitFiscalInvoices} onChange={liga('splitFiscalInvoices')} rotulo='Separar NFS-e/NFCom' dica='2 faturas por mês: uma por nota' />
          <Interruptor id='manualFiscalEmission' ligado={!!f.manualFiscalEmission} onChange={liga('manualFiscalEmission')} rotulo='NF Manual' dica='aprovar a fatura NÃO emite a NF: emitir pela NF parcial ou Regerar' />
          <Interruptor id='hideNfsePeriod' ligado={!!f.hideNfsePeriod} onChange={liga('hideNfsePeriod')} rotulo='Ocultar Período' dica='sem o período na fatura e na NFS-e' />
        </Grupo>
        <Grupo titulo='Como chega ao cliente'>
          <div>
            <label htmlFor='emailDeliveryType' className='block text-sm text-slate-200 mb-1'>Envio por e-mail</label>
            <select id='emailDeliveryType' value={f.emailDeliveryType || 'nf_fatura_boleto'} onChange={(e) => setForm({ ...form, emailDeliveryType: e.target.value })} className={`w-full px-4 py-2.5 ${campo} rounded-lg`}>
              {ENVIO_POR_EMAIL.map((o) => <option key={o.valor} value={o.valor}>{o.rotulo}</option>)}
            </select>
          </div>
          <Interruptor id='includeXmlInEmail' ligado={!!f.includeXmlInEmail} onChange={liga('includeXmlInEmail')} rotulo='Incluir XML' dica='envia o XML fiscal no e-mail' trava={motivoDaTrava('includeXmlInEmail', f)} />
          <Interruptor id='zipFiscalAttachments' ligado={!!f.zipFiscalAttachments} onChange={liga('zipFiscalAttachments')} rotulo='Zipar NFs' dica='agrupa NFs e XMLs num ZIP no e-mail' trava={motivoDaTrava('zipFiscalAttachments', f)} />
        </Grupo>
        <Grupo titulo='Quando aprovar'>
          <Interruptor id='approvalDeferred' ligado={adiada} rotulo='Aprovação adiada' dica='retém a fatura até a janela do órgão pagador'
            onChange={(v) => setForm({
              ...form,
              // Desligar limpa os dois: motivo vazio é o que o backend lê como "não retém nada".
              approvalDeferredReason: v ? (form.approvalDeferredReason || '') : null,
              approvalDeferredDay: v ? form.approvalDeferredDay : null,
            })} />
          {/* Só com o interruptor ligado. A geração mensal carimba a retenção na fatura; o lote pula a retida e a
              aprovação individual exige liberação explícita. */}
          {adiada && (
            <div className='border border-amber-500/30 bg-amber-500/5 rounded-lg p-3'>
              <p className='text-xs text-slate-400 mb-3'>
                Com dia preenchido, a retenção libera sozinha na data. Sem dia, cada fatura precisa ser
                liberada à mão (ex.: cliente que valida antes de autorizar o faturamento).
              </p>
              <div className='grid grid-cols-1 gap-3'>
                <div>
                  <label className='block text-xs text-slate-400 mb-1'>Dia de liberação</label>
                  <input type='number' min={1} max={31} placeholder='ex: 15' value={form.approvalDeferredDay ?? ''}
                    onChange={(e) => setForm({ ...form, approvalDeferredDay: e.target.value ? parseInt(e.target.value) : null })}
                    className={`w-full px-4 py-2.5 ${campo} rounded-lg`} />
                </div>
                <div>
                  <label className='block text-xs text-slate-400 mb-1'>Motivo da retenção</label>
                  <input type='text' maxLength={200} placeholder='ex: Janela de envio do órgão: aprovar a partir do dia 15'
                    value={form.approvalDeferredReason || ''}
                    onChange={(e) => setForm({ ...form, approvalDeferredReason: e.target.value })}
                    className={`w-full px-4 py-2.5 ${campo} rounded-lg`} />
                </div>
              </div>
            </div>
          )}
        </Grupo>
      </div>
    </div>
  );
}
